import React, { useCallback, useEffect, useState } from 'react';
import {
  User,
  Mail,
  Phone,
  Edit2,
  ChevronLeft,
  Crown,
  Star,
  ShoppingBag,
  Heart,
  Home,
  Gift,
  LogOut,
  ChevronRight,
  FileText,
  Calendar,
  AlertCircle,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '../api/auth';
import { formatINR } from '../utils/currency';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

interface ProfileStats {
  totalOrders?: number;
  totalSpent?: number;
  wishlistCount?: number;
  addressesCount?: number;
}

interface ProfileUser {
  _id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  gender?: string;
  dateOfBirth?: string;
  createdAt?: string;
  stats?: ProfileStats;
}

interface ActiveCoupon {
  _id: string;
  code: string;
  description?: string;
  discountPercentage?: number;
  discountAmount?: number;
  minPurchaseAmount?: number;
  expiryDate?: string;
}

interface ProfileForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  gender: string;
  dateOfBirth: string;
}

function getInitials(user: ProfileUser) {
  return `${user.firstName?.charAt(0) || ''}${user.lastName?.charAt(0) || ''}`.toUpperCase() || 'U';
}

function getTierLabel(orders: number) {
  if (orders > 20) return 'Platinum';
  if (orders > 10) return 'Gold';
  if (orders > 5) return 'Silver';
  return 'Bronze';
}

function formatDob(value?: string) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
}

function formatDobInput(value?: string) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
}

function validateProfileForm(form: ProfileForm): string | null {
  if (!form.firstName.trim() || !form.lastName.trim()) {
    return 'First and last name are required.';
  }
  const phoneDigits = form.phone.replace(/\D/g, '');
  if (phoneDigits.length < 10) {
    return 'Enter a valid 10-digit phone number.';
  }
  if (form.gender && !['male', 'female', 'other'].includes(form.gender)) {
    return 'Please select a valid gender.';
  }
  if (form.dateOfBirth) {
    const dob = new Date(form.dateOfBirth);
    if (Number.isNaN(dob.getTime()) || dob > new Date()) {
      return 'Please enter a valid date of birth.';
    }
  }
  return null;
}

function filterActiveCoupons(list: ActiveCoupon[]): ActiveCoupon[] {
  const now = Date.now();
  return list.filter((c) => {
    if (!c.expiryDate) return true;
    return new Date(c.expiryDate).getTime() > now;
  });
}

function couponLabel(coupon: ActiveCoupon) {
  if (coupon.description) return coupon.description;
  if (coupon.discountPercentage) return `${coupon.discountPercentage}% off your order`;
  if (coupon.discountAmount) return `${formatINR(coupon.discountAmount)} off`;
  return 'Special offer for you';
}

function CouponCard({ coupon }: { coupon: ActiveCoupon }) {
  const expiry = coupon.expiryDate
    ? new Date(coupon.expiryDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  return (
    <div className="rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <span className="inline-block rounded bg-amber-500 px-2 py-1 text-xs font-bold text-white">{coupon.code}</span>
          <p className="mt-2 text-sm font-medium text-gray-800">{couponLabel(coupon)}</p>
          <p className="mt-1 text-xs text-gray-500">
            Min order {formatINR(coupon.minPurchaseAmount || 0)}
            {expiry ? ` · Valid till ${expiry}` : ''}
          </p>
        </div>
        <Gift className="h-5 w-5 shrink-0 text-amber-500" />
      </div>
    </div>
  );
}

function RewardsEmptyState() {
  return (
    <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-center">
      <Gift className="mx-auto mb-2 h-8 w-8 text-gray-300" />
      <p className="text-sm font-medium text-gray-700">No active offers right now</p>
      <p className="mt-1 text-xs text-gray-500">Check back later for new coupons and deals.</p>
    </div>
  );
}

function InfoTile({
  icon: Icon,
  label,
  value,
  iconClass,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  iconClass: string;
}) {
  return (
    <div className="flex items-start gap-3 rounded-xl bg-gray-50 p-3">
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${iconClass}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wider text-gray-500">{label}</p>
        <p className="font-semibold text-gray-900 break-words">{value}</p>
      </div>
    </div>
  );
}

const UserProfile: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, logout } = useAuth();

  const [user, setUser] = useState<ProfileUser | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [couponsLoading, setCouponsLoading] = useState(true);
  const [coupons, setCoupons] = useState<ActiveCoupon[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  const [formData, setFormData] = useState<ProfileForm>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    gender: '',
    dateOfBirth: '',
  });

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }, []);

  const applyUserToForm = useCallback((userData: ProfileUser) => {
    setUser(userData);
    setFormData({
      firstName: userData.firstName || '',
      lastName: userData.lastName || '',
      email: userData.email || '',
      phone: userData.phone || '',
      gender: userData.gender || '',
      dateOfBirth: formatDobInput(userData.dateOfBirth),
    });
  }, []);

  const fetchUserProfile = useCallback(async () => {
    try {
      setLoading(true);
      setPageError(null);
      const response = await fetch(`${API_URL}/users/me`, { headers: authHeaders() });

      if (response.status === 401) {
        await logout();
        navigate('/login', { replace: true });
        return;
      }

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to load profile');
      }

      applyUserToForm(data.data);
    } catch (err) {
      const fallback = authService.getCurrentUser();
      if (fallback) {
        applyUserToForm(fallback as ProfileUser);
      } else {
        setPageError(err instanceof Error ? err.message : 'Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  }, [applyUserToForm, authHeaders, logout, navigate]);

  const fetchCoupons = useCallback(async () => {
    try {
      setCouponsLoading(true);
      const response = await fetch(`${API_URL}/coupons/active`, { headers: authHeaders() });
      const data = await response.json();
      if (response.ok && data.success) {
        setCoupons(filterActiveCoupons(data.data || []));
      } else {
        setCoupons([]);
      }
    } catch {
      setCoupons([]);
    } finally {
      setCouponsLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) return;
    fetchUserProfile();
    fetchCoupons();
  }, [authLoading, isAuthenticated, fetchUserProfile, fetchCoupons]);

  useEffect(() => {
    if (!saveSuccess) return;
    const t = window.setTimeout(() => setSaveSuccess(null), 4000);
    return () => window.clearTimeout(t);
  }, [saveSuccess]);

  const scrollToRewards = () => {
    const targetId = window.matchMedia('(min-width: 1024px)').matches
      ? 'rewards-section-desktop'
      : 'rewards-section-mobile';
    window.setTimeout(() => {
      document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setSaveSuccess(null);

    const validationError = validateProfileForm(formData);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    try {
      setSaving(true);
      const payload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phone: formData.phone.trim(),
        gender: formData.gender || undefined,
        dateOfBirth: formData.dateOfBirth || undefined,
      };

      const response = await fetch(`${API_URL}/users/me`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.status === 401) {
        await logout();
        navigate('/login', { replace: true });
        return;
      }

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to update profile');
      }

      applyUserToForm(data.data);
      localStorage.setItem('user', JSON.stringify(data.data));
      setIsEditing(false);
      setSaveSuccess('Profile updated successfully.');
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    if (user) applyUserToForm(user);
    setFormError(null);
    setIsEditing(false);
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-indigo-600" />
          <p className="mt-3 text-sm font-medium text-gray-600">Loading your account…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
          <h2 className="mt-4 text-xl font-bold text-gray-900">Please sign in</h2>
          <p className="mt-2 text-gray-600">{pageError || 'Sign in to view and manage your profile.'}</p>
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="mt-6 rounded-xl bg-indigo-600 px-6 py-2.5 font-semibold text-white hover:bg-indigo-700"
          >
            Sign in
          </button>
        </div>
      </div>
    );
  }

  const initials = getInitials(user);
  const memberSince = user.createdAt
    ? new Date(user.createdAt).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })
    : '—';
  const tier = getTierLabel(user.stats?.totalOrders || 0);

  const quickLinks = [
    { to: '/orders', icon: ShoppingBag, label: 'My Orders', sub: `${user.stats?.totalOrders || 0} orders`, color: 'bg-blue-100 text-blue-600' },
    { to: '/my-bills', icon: FileText, label: 'My Bills', sub: 'View & download', color: 'bg-indigo-100 text-indigo-600' },
    { to: '/wishlist', icon: Heart, label: 'Wishlist', sub: `${user.stats?.wishlistCount || 0} items`, color: 'bg-pink-100 text-pink-600' },
    { to: '/addresses', icon: Home, label: 'Addresses', sub: `${user.stats?.addressesCount || 0} saved`, color: 'bg-purple-100 text-purple-600' },
  ];

  const renderRewardsPanel = (sectionId: string) => (
    <div id={sectionId} className="scroll-mt-24 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between gap-2">
        <h3 className="flex items-center text-lg font-bold text-gray-900">
          <Gift className="mr-2 h-5 w-5 text-amber-600" />
          Offers & coupons
        </h3>
        {!couponsLoading && (
          <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
            {coupons.length} active
          </span>
        )}
      </div>

      {couponsLoading ? (
        <div className="flex items-center justify-center py-8 text-sm text-gray-500">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading offers…
        </div>
      ) : coupons.length === 0 ? (
        <RewardsEmptyState />
      ) : (
        <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
          {coupons.map((coupon) => (
            <CouponCard key={coupon._id || coupon.code} coupon={coupon} />
          ))}
        </div>
      )}
    </div>
  );

  if (isEditing) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="sticky top-0 z-20 border-b border-gray-200 bg-white">
          <div className="mx-auto flex h-14 max-w-2xl items-center justify-between px-4">
            <button type="button" onClick={cancelEdit} className="rounded-lg p-2 text-gray-600 hover:bg-gray-100" aria-label="Back">
              <ChevronLeft className="h-6 w-6" />
            </button>
            <h1 className="text-lg font-bold text-gray-900">Edit profile</h1>
            <div className="w-10" />
          </div>
        </div>

        <div className="mx-auto max-w-2xl px-4 py-6">
          {formError && (
            <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              {formError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">First name *</label>
                <input
                  type="text"
                  required
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">Last name *</label>
                <input
                  type="text"
                  required
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={formData.email}
                readOnly
                className="w-full cursor-not-allowed rounded-xl border border-gray-200 bg-gray-100 px-4 py-2.5 text-gray-600"
              />
              <p className="mt-1 text-xs text-gray-500">Email cannot be changed here. Contact support if needed.</p>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">Phone *</label>
              <input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                placeholder="10-digit mobile number"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">Gender</label>
                <select
                  value={formData.gender}
                  onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                  className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                >
                  <option value="">Prefer not to say</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">Date of birth</label>
                <input
                  type="date"
                  value={formData.dateOfBirth}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                  className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={cancelEdit}
                disabled={saving}
                className="flex-1 rounded-xl border border-gray-200 py-3 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-indigo-600 py-3 font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24 lg:pb-10">
      {/* Mobile header */}
      <div className="sticky top-0 z-20 border-b border-gray-200 bg-white lg:hidden">
        <div className="flex h-14 items-center justify-between px-4">
          <Link to="/" className="rounded-lg p-2 text-gray-600 hover:bg-gray-100" aria-label="Back to home">
            <ChevronLeft className="h-6 w-6" />
          </Link>
          <h1 className="text-lg font-bold text-gray-900">My account</h1>
          <button type="button" onClick={() => setIsEditing(true)} className="rounded-lg p-2 text-indigo-600 hover:bg-indigo-50" aria-label="Edit profile">
            <Edit2 className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Desktop header */}
      <div className="hidden border-b border-gray-200 bg-white lg:block">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4">
          <div>
            <Link to="/" className="text-sm text-gray-500 hover:text-gray-800">
              ← Back to store
            </Link>
            <h1 className="mt-1 text-2xl font-bold text-gray-900">My account</h1>
          </div>
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            <Edit2 className="h-4 w-4" />
            Edit profile
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-5xl space-y-4 px-4 py-4 lg:space-y-6 lg:py-6">
        {saveSuccess && (
          <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            {saveSuccess}
          </div>
        )}

        {pageError && (
          <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {pageError}
          </div>
        )}

        {/* Hero card */}
        <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 p-5 text-white shadow-lg">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 text-xl font-bold">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h2 className="truncate text-xl font-bold">
                  {user.firstName} {user.lastName}
                </h2>
                <Crown className="h-4 w-4 shrink-0 text-amber-400" />
              </div>
              <p className="truncate text-sm text-slate-300">{user.email}</p>
              <p className="mt-1 flex items-center gap-2 text-xs text-slate-400">
                <Star className="h-3 w-3 text-amber-400" />
                {tier} member · Since {memberSince}
              </p>
            </div>
          </div>
        </div>

        {/* Quick stats — mobile */}
        <div className="grid grid-cols-4 gap-2 rounded-2xl border border-gray-100 bg-white p-3 shadow-sm lg:hidden">
          {[
            { label: 'Orders', value: user.stats?.totalOrders || 0, to: '/orders' },
            { label: 'Wishlist', value: user.stats?.wishlistCount || 0, to: '/wishlist' },
            { label: 'Addresses', value: user.stats?.addressesCount || 0, to: '/addresses' },
            { label: 'Offers', value: coupons.length, action: scrollToRewards },
          ].map((stat) =>
            stat.to ? (
              <Link key={stat.label} to={stat.to} className="text-center">
                <p className="text-lg font-bold text-gray-900">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </Link>
            ) : (
              <button key={stat.label} type="button" onClick={stat.action} className="text-center">
                <p className="text-lg font-bold text-gray-900">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </button>
            )
          )}
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            {/* Quick links */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {quickLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
                >
                  <div className={`mb-3 flex h-11 w-11 items-center justify-center rounded-xl ${link.color.split(' ')[0]}`}>
                    <link.icon className={`h-5 w-5 ${link.color.split(' ')[1]}`} />
                  </div>
                  <p className="text-sm font-semibold text-gray-900">{link.label}</p>
                  <p className="text-xs text-gray-500">{link.sub}</p>
                </Link>
              ))}
              <button
                type="button"
                onClick={scrollToRewards}
                className="rounded-2xl border border-gray-100 bg-white p-4 text-left shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-amber-100">
                  <Gift className="h-5 w-5 text-amber-600" />
                </div>
                <p className="text-sm font-semibold text-gray-900">Offers</p>
                <p className="text-xs text-gray-500">{coupons.length} active</p>
              </button>
            </div>

            {/* Personal info */}
            <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
              <div className="mb-5 flex items-center justify-between gap-3">
                <h3 className="text-lg font-bold text-gray-900">Personal information</h3>
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-indigo-600 hover:bg-indigo-50"
                >
                  <Edit2 className="h-4 w-4" />
                  Edit
                </button>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <InfoTile icon={User} label="Full name" value={`${user.firstName || ''} ${user.lastName || ''}`.trim()} iconClass="bg-blue-100 text-blue-600" />
                <InfoTile icon={Mail} label="Email" value={user.email || '—'} iconClass="bg-violet-100 text-violet-600" />
                <InfoTile icon={Phone} label="Phone" value={user.phone || 'Not provided'} iconClass="bg-emerald-100 text-emerald-600" />
                <InfoTile
                  icon={User}
                  label="Gender"
                  value={user.gender ? user.gender.charAt(0).toUpperCase() + user.gender.slice(1) : 'Not set'}
                  iconClass="bg-pink-100 text-pink-600"
                />
                <InfoTile icon={Calendar} label="Date of birth" value={formatDob(user.dateOfBirth) || 'Not set'} iconClass="bg-amber-100 text-amber-600" />
              </div>
            </div>

            {/* Mobile menu */}
            <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm lg:hidden">
              {quickLinks.map((link, i) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`flex items-center justify-between px-4 py-4 hover:bg-gray-50 ${i < quickLinks.length - 1 ? 'border-b border-gray-100' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${link.color.split(' ')[0]}`}>
                      <link.icon className={`h-5 w-5 ${link.color.split(' ')[1]}`} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{link.label}</p>
                      <p className="text-xs text-gray-500">{link.sub}</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </Link>
              ))}
              <button
                type="button"
                onClick={scrollToRewards}
                className="flex w-full items-center justify-between px-4 py-4 hover:bg-gray-50"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100">
                    <Gift className="h-5 w-5 text-amber-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-900">Offers & coupons</p>
                    <p className="text-xs text-gray-500">{coupons.length} available</p>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </button>
            </div>

            <div className="lg:hidden">{renderRewardsPanel('rewards-section-mobile')}</div>
          </div>

          <div className="hidden space-y-4 lg:block">
            {renderRewardsPanel('rewards-section-desktop')}
            <button
              type="button"
              onClick={() => logout().then(() => navigate('/login'))}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-red-200 py-3 font-medium text-red-600 hover:bg-red-50"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={() => logout().then(() => navigate('/login'))}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-red-200 bg-white py-3 font-medium text-red-600 hover:bg-red-50 lg:hidden"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      </div>
    </div>
  );
};

export default UserProfile;
